import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Debounced catalog search + pick, with private-add and request-to-promote
const AddSupplyModal = ({ horseId, onClose, onSaved }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [dose, setDose] = useState('');
  const [frequency, setFrequency] = useState('');
  const [notes, setNotes] = useState('');
  const [requestPromotion, setRequestPromotion] = useState(false);
  const [saving, setSaving] = useState(false);
  // Reorder reminder settings
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [reorderChoice, setReorderChoice] = useState('once');
  const [cycleDays, setCycleDays] = useState(30);
  const [cycleCustom, setCycleCustom] = useState('');
  const [leadDays, setLeadDays] = useState(5);
  const [leadCustom, setLeadCustom] = useState('');
  const [recurring, setRecurring] = useState(true);
  const [trackUsage, setTrackUsage] = useState(true);
  const debounceRef = useRef(null);

  const runSearch = useCallback((q) => {
    setSearching(true);
    api.get(`/supplies/search?q=${encodeURIComponent(q)}`)
      .then(r => setResults(r.data.results || []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const noMatch = query.trim() && !searching && results.length === 0;

  const handleSave = async () => {
    const name = picked ? picked.name : query.trim();
    if (!name) return toast.error('Choose or type a supply');
    setSaving(true);
    try {
      // If it's a rogue item not from catalog, save it to the user's private list (optionally request promotion)
      if (!picked) {
        await api.post('/supplies/private', { name, request_promotion: requestPromotion });
      }
      const effectiveCycle = cycleDays === 'custom' ? parseInt(cycleCustom) || 30 : cycleDays;
      const effectiveLead = leadDays === 'custom' ? parseInt(leadCustom) || 5 : leadDays;
      await api.post(`/supplies/horse/${horseId}`, {
        name, subcategory: picked?.subcategory, dose, frequency, notes,
        reorder_enabled: reorderEnabled,
        reorder_cycle_days: effectiveCycle,
        reorder_lead_days: effectiveLead,
        reorder_recurring: recurring,
        reorder_track_usage: trackUsage,
      });
      toast.success('Supply added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>Add supply</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        {!picked && (
          <>
            <div className="form-group">
              <label className="form-label">Search supplies</label>
              <input className="form-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Start typing, e.g. ShowSheen" autoComplete="off" autoFocus />
            </div>

            {searching && <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>Searching…</div>}

            {results.length > 0 && (
              <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
                {results.map(r => (
                  <div key={r.id} onClick={() => setPicked(r)}
                    style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
                      {r.subcategory && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.subcategory}</div>}
                    </div>
                    {r.source === 'private' && <span className="badge badge-gray">Yours</span>}
                  </div>
                ))}
              </div>
            )}

            {noMatch && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>No match for "<strong>{query.trim()}</strong>". You can add it as your own item.</div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={requestPromotion} onChange={e => setRequestPromotion(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
                  Also request this be added to the master catalog
                </label>
              </div>
            )}
          </>
        )}

        {(picked || noMatch) && (
          <>
            <div style={{ background: 'var(--cr-primary-light)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{picked ? picked.name : query.trim()}</div>
              {picked?.subcategory && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{picked.subcategory}</div>}
              <button onClick={() => { setPicked(null); }} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12, marginTop: 4, padding: 0 }}>Change</button>
            </div>
            <div className="form-group"><label className="form-label">Quantity</label><input className="form-input" value={dose} onChange={e => setDose(e.target.value)} placeholder="e.g. 2 rolls" /></div>
            <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} /></div>

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 12, marginBottom: 10 }}>
              <div className="form-group">
                <label className="form-label">How often do you reorder?</label>
                <select className="form-input" value={reorderChoice}
                  onChange={e => {
                    const v = e.target.value;
                    setReorderChoice(v);
                    if (v === 'once') { setReorderEnabled(false); }
                    else {
                      setReorderEnabled(true);
                      if (v === 'learn') { setTrackUsage(true); setCycleDays(30); }
                      else { setCycleDays(parseInt(v)); }
                    }
                  }}>
                  <option value="once">One-time — no reminder</option>
                  <option value="30">Monthly</option>
                  <option value="75">Every 2–3 months</option>
                  <option value="180">Twice a year</option>
                  <option value="learn">Not sure — remind me based on my orders</option>
                </select>
              </div>
            </div>

            {reorderEnabled && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Remind me this far ahead</label>
                  <select className="form-input" value={leadDays} onChange={e => setLeadDays(e.target.value === 'custom' ? 'custom' : parseInt(e.target.value))}>
                    <option value={3}>3 days before</option>
                    <option value={5}>5 days before</option>
                    <option value={7}>7 days before</option>
                    <option value={14}>14 days before</option>
                    <option value="custom">Custom…</option>
                  </select>
                  {leadDays === 'custom' && <input className="form-input" type="number" value={leadCustom} onChange={e => setLeadCustom(e.target.value)} placeholder="Days" style={{ marginTop: 6 }} />}
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '4px 0' }}>
                  <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
                  Recurring (keep reminding each cycle)
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, padding: '4px 0' }}>
                  <input type="checkbox" checked={trackUsage} onChange={e => setTrackUsage(e.target.checked)} style={{ accentColor: 'var(--cr-primary)', marginTop: 2 }} />
                  <span>Track my actual usage — learns from your feedback and adjusts the timing if you run out sooner or end up with extra.</span>
                </label>
              </div>
            )}

            <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Add to horse'}</button>
          </>
        )}
      </div>
    </div>
  );
};

const SuppliesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [h, s] = await Promise.all([api.get(`/horses/${id}`), api.get(`/supplies/horse/${id}`)]);
      setHorse(h.data); setList(s.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const remove = async (sid) => {
    if (!window.confirm('Remove this supply?')) return;
    try { await api.delete(`/supplies/supply/${sid}`); load(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(`/horses/${id}`)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: 'white', fontSize: 17 }}>Supplies</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{horse?.name}</p>
          </div>
          <button onClick={() => setAdding(true)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 13, borderRadius: 8, padding: '6px 14px', fontWeight: 500 }}>+ Add</button>
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {list.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🧴</div>
            <h3>No supplies yet</h3>
            <p>Search the catalog to add what {horse?.name} is on.</p>
          </div>
        ) : (
          list.map(s => (
            <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>🧴</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {[s.dose, s.frequency, s.subcategory].filter(Boolean).join(' · ')}
                </div>
                {s.notes && <div style={{ fontSize: 12, marginTop: 2 }}>{s.notes}</div>}
              </div>
              <button onClick={() => remove(s.id)} className="btn btn-danger btn-sm">Remove</button>
            </div>
          ))
        )}
      </div>

      <div style={{ padding: '0 12px' }}>
        <button className="btn btn-secondary btn-full" onClick={() => navigate('/supplies?view=reorder')}>
          🛒 Order supplies
        </button>
      </div>

      {adding && <AddSupplyModal horseId={id} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
};

export default SuppliesPage;
